import React, { useEffect, useState, useMemo } from 'react';
import withRouter from '../../components/Common/withRouter';
import TableContainer from '../../components/Common/TableContainer';
import Spinners from '../../components/Common/Spinner';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useAuthStore } from '../../zustand-store/auth.store';
import { ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import VerticalLayout from '../../components/VerticalLayout';
import { todayAppointmentData } from '../../axios/appointment/index.api';
import * as XLSX from 'xlsx'; // Excel Export

const TodayBookings = () => {
  document.title = 'Appointments Report | Seamless Slot';

  const locationId = useAuthStore((state) => state.locationId);
  const businessId = useAuthStore((state) => state.businessId);
  const [pageSize, setPageSize] = useState(100);

  const ContactsProperties = createSelector(
    (state) => state.contacts,
    (Contacts) => ({
      users: Contacts?.users,
      loading: Contacts?.loading
    })
  );

  const { loading } = useSelector(ContactsProperties);
  const [isLoading, setLoading] = useState(loading);
  const [data, setData] = useState([]);

  useEffect(() => {
    localStorage.setItem('path', '/reports/todayBookingsReport');
    getData();
  }, [locationId]);

  const getData = async () => {
    setLoading(true);
    const res = await todayAppointmentData(parseInt(businessId));
    setData(res?.data?.data?.data || []);
    setLoading(false);
  };

  const columns = useMemo(
    () => [
      {
        header: 'Location',
        accessorKey: 'locationName',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Confirmed Today',
        accessorKey: 'totalConfirmedBookingsToday',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Pending Today',
        accessorKey: 'totalPendingBookingsToday',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Confirmed Tomorrow',
        accessorKey: 'totalConfirmedBookingsTomorrow',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Pending Tomorrow',
        accessorKey: 'totalPendingBookingsTomorrow',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Created Today',
        accessorKey: 'totalCreatedAppointmentsToday',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Recent Booked',
        accessorKey: 'lastCreatedAppointmentOverall',
        enableColumnFilter: false,
        enableSorting: true
      }
    ],
    [businessId]
  );

  const handleExport = () => {
    if (data.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        Location: item.locationName,
        'Confirmed Today': item.totalConfirmedBookingsToday,
        'Pending Today': item.totalPendingBookingsToday,
        'Confirmed Tomorrow': item.totalConfirmedBookingsTomorrow,
        'Pending Tomorrow': item.totalPendingBookingsTomorrow,
        'Created Today': item.totalCreatedAppointmentsToday,
        'Recent Booked': item.lastCreatedAppointmentOverall
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Today Bookings');
    XLSX.writeFile(workbook, 'Today_Bookings_Report.xlsx');
  };

  const getTotal = (key) => {
    return data.reduce((total, item) => total + (parseInt(item[key]) || 0), 0);
  };

  const pricePerBooking = 50; // You can change this
  const confirmedTodayTotal = getTotal('totalConfirmedBookingsToday');
  const confirmedTomorrowTotal = getTotal('totalConfirmedBookingsTomorrow');

  return (
    <VerticalLayout>
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Appointments Report" breadcrumbItem="Appointments Report" />
            {isLoading ? (
              <Spinners setLoading={setLoading} />
            ) : (
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-end mb-3">
                        <button className="btn btn-primary" onClick={handleExport}>
                          Export to Excel
                        </button>
                      </div>
                      {/* Totals Section */}
                      <Card className="mt-4">
                        <CardBody>
                          <h5>Summary</h5>
                          <Row>
                            <Col md={4}>
                              <p>
                                <strong>Confirmed Today:</strong> {confirmedTodayTotal}
                              </p>
                              <p>
                                <strong>Price:</strong> £
                                {(confirmedTodayTotal * pricePerBooking)?.toFixed(2)} approx
                              </p>
                            </Col>
                            <Col md={4}>
                              <p>
                                <strong>Pending Today:</strong>{' '}
                                {getTotal('totalPendingBookingsToday')}
                              </p>
                            </Col>
                            <Col md={4}>
                              <p>
                                <strong>Created Today:</strong>{' '}
                                {getTotal('totalCreatedAppointmentsToday')}
                              </p>
                            </Col>
                          </Row>
                          <Row className="mt-3">
                            <Col md={4}>
                              <p>
                                <strong>Confirmed Tomorrow:</strong> {confirmedTomorrowTotal}
                              </p>
                              <p>
                                <strong>Price:</strong> £
                                {(confirmedTomorrowTotal * pricePerBooking)?.toFixed(2)} approx
                              </p>
                            </Col>
                            <Col md={4}>
                              <p>
                                <strong>Pending Tomorrow:</strong>{' '}
                                {getTotal('totalPendingBookingsTomorrow')}
                              </p>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={true}
                        isPagination={false}
                        isServerPagination={true}
                        SearchPlaceholder="Search..."
                        isCustomPageSize={true}
                        buttonClass="btn bg-primary rounded-pill addContact-modal mb-2 px-4"
                        tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                        theadClass="table-light"
                        paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                        pagination="pagination"
                        pageSizeOptions={[100, 200]}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        tourId="open-add-service-modal"
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </Container>
        </div>
        <ToastContainer />
      </React.Fragment>
    </VerticalLayout>
  );
};

export default withRouter(TodayBookings);
